import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { CommunicationHistory } from '@models/communications/communication-history.model';
import {
  CommunicationSettingType,
  EmailCommunicationSettings,
  SmsCommunicationSettings,
} from '@models/communications/communication-settings';
import { CommunicationHistorySummary } from '@models/communications/communicaton-history-summary.model';
import { EmailCommunicationSetting } from '@models/communications/email-notifications-settings.model';
import { MailjetEmailTemplate } from '@models/communications/mailjet-email-template.model';
import { CommunicationsSettingBase } from '@models/communications/notifications-settings-base.model';
import { RetentionCampaignsSettings } from '@models/communications/retention-campaigns-settings.model';
import { SmsCommunicationSetting } from '@models/communications/sms-notifications-settings.model';
import { TestEmail } from '@models/communications/test-email.model';
import { TestSms } from '@models/communications/test-sms.model';
import { PagedDataResult } from '@models/paged-data-result.model';
import { DataSourceRequestState } from '@progress/kendo-data-query';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class CommunicationsService {
  communicationsSettingsUpdated = new Subject();
  refreshRequired = false;

  constructor(private http: HttpClient) {}

  getLatestReviewRequest(patientId: number): Observable<CommunicationHistory | null> {
    return this.http.get<CommunicationHistory>(
      `${environment.baseUrl}api/Communications/GetLatestReviewRequest/${patientId}`
    );
  }

  getEmailTemplates(): Observable<MailjetEmailTemplate[]> {
    return this.http.get<MailjetEmailTemplate[]>(environment.baseUrl + 'api/Communications/GetAllEmailTemplates');
  }

  getEmailTemplateDetails(templateId: number): Observable<MailjetEmailTemplate> {
    return this.http
      .get<MailjetEmailTemplate>(environment.baseUrl + `api/Communications/GetEmailTemplateDetails/${templateId}`)
      .pipe(
        map((template) => {
          if (template && template.htmlTemplate && template.htmlTemplate.indexOf('</head>') != -1) {
            template.htmlTemplate = template.htmlTemplate.replace(
              '</head>',
              '<style type="text/css">::-webkit-scrollbar {width: 18px;}::-webkit-scrollbar-track {background-color: transparent;} ::-webkit-scrollbar-thumb {background-color: #d3d4d5; border-radius: 18px; border: 6px solid transparent; background-clip: content-box;} ::-webkit-scrollbar-thumb:hover{background-color: #b2b2b2;}</style></head>'
            );
          }
          return template;
        })
      );
  }

  getEmailSettingById(settingId: number): Observable<EmailCommunicationSetting> {
    return this.http
      .get<EmailCommunicationSetting>(
        environment.baseUrl + 'api/Communications/GetEmailNotificationsSettingsById/' + settingId
      )
      .pipe(map((_) => this.parseDurations(new EmailCommunicationSetting(_))));
  }

  getEmailNotificationsSettings(): Observable<EmailCommunicationSettings> {
    return this.http
      .get<EmailCommunicationSettings>(environment.baseUrl + 'api/Communications/GetEmailNotificationsSettings')
      .pipe(map((_) => this.parseDurations(_)));
  }

  getEmailNotificationsSettingByType(settingType: CommunicationSettingType): Observable<EmailCommunicationSetting> {
    return this.http
      .get<EmailCommunicationSetting>(
        environment.baseUrl + 'api/Communications/GetEmailNotificationsSettingsByType/' + settingType
      )
      .pipe(map((_) => this.parseDurations(new EmailCommunicationSetting(_))));
  }

  getSmsNotificationsSettings(): Observable<SmsCommunicationSettings> {
    return this.http
      .get<SmsCommunicationSettings>(environment.baseUrl + 'api/Communications/GetSmsNotificationsSettings')
      .pipe(map((_) => this.parseDurations(_)));
  }

  getSmsNotificationsSettingsByType(settingType: CommunicationSettingType): Observable<SmsCommunicationSetting> {
    return this.http
      .get<SmsCommunicationSetting>(
        environment.baseUrl + 'api/Communications/GetSmsNotificationsSettingsByType/' + settingType
      )
      .pipe(map((_) => this.parseDurations(new SmsCommunicationSetting(_))));
  }

  getRetentionCampaignsSettings(): Observable<RetentionCampaignsSettings> {
    return this.http.get<RetentionCampaignsSettings>(
      environment.baseUrl + 'api/Communications/GetRetentionCampaignsSettings'
    );
  }

  updateEmailNotificationsSettings(data: EmailCommunicationSetting) {
    return this.http.post(environment.baseUrl + 'api/Communications/UpdateEmailNotificationsSettings', data);
  }

  updateSmsNotificationsSettings(data: SmsCommunicationSetting) {
    return this.http.post(environment.baseUrl + 'api/Communications/UpdateSmsNotificationsSettings', data);
  }

  updateRetentionCampaignsSettings(data: RetentionCampaignsSettings) {
    return this.http.post(environment.baseUrl + 'api/Communications/UpdateRetentionCampaignsSettings', data);
  }

  getHistoryForPatient(
    patientId: number,
    state: DataSourceRequestState
  ): Observable<PagedDataResult<CommunicationHistory>> {
    const url = environment.baseUrl + `api/Communications/GetHistory`;
    const data = {
      patientId,
      state,
    };
    return this.http.post<PagedDataResult<CommunicationHistory>>(url, data);
  }

  getHistory(
    state: DataSourceRequestState,
    startFilterDate: Date,
    endFilterDate: Date,
    searchText: string
  ): Observable<PagedDataResult<CommunicationHistorySummary>> {
    const url = environment.baseUrl + `api/Communications/GetHistorySummary`;
    const data = {
      state,
      endFilterDate,
      startFilterDate,
      searchText,
    };
    return this.http.post<PagedDataResult<CommunicationHistorySummary>>(url, data);
  }

  sendTestEmail(data: TestEmail) {
    return this.http.post(environment.baseUrl + 'api/Communications/SendTestEmail', data);
  }

  sendTestEmailSes(data: TestEmail) {
    return this.http.post(environment.baseUrl + 'api/Communications/SendTestEmailSes', data);
  }

  sendTestSms(data: TestSms) {
    return this.http.post(environment.baseUrl + 'api/Communications/SendTestSms', data);
  }

  sendTestCampaignSms(data: TestSms) {
    return this.http.post(environment.baseUrl + 'api/Communications/SendTestCampaignSms', data);
  }

  private parseDurations<T extends CommunicationsSettingBase | EmailCommunicationSettings | SmsCommunicationSettings>(
    s: T
  ): T {
    if (s instanceof CommunicationsSettingBase) {
      s.workerStartTime = moment.duration(s.workerStartTime);
      s.workerEndTime = moment.duration(s.workerEndTime);
      return s;
    }
    Object.values(s).forEach((setting) => {
      if (setting) {
        setting.workerStartTime = moment.duration(setting.workerStartTime);
        setting.workerEndTime = moment.duration(setting.workerEndTime);
      }
    });
    return s;
  }
}
